/**
 * iter87.225 — NBA Champions Pick modal, sibling of
 * `MlbChampionsPickModal.jsx`. A single NBA champion pick, +10 flat
 * bonus, locked once the Conference Finals matchups are set (one
 * round earlier than MLB's Championship Series lock, since NBA has
 * the extra Play-In + First Round ahead of it).
 *
 * iter87.263 (user-reported) — Restyled to match the format of the
 * non-playoff Champion Bonus pick (`KnockoutPickModal.jsx`): a "Your
 * pick" status card + a card-grid "Change to" section, instant-save
 * on tap (no separate Save button), cyber accent, +N pts folded into
 * the title instead of a standalone green tag.
 */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Crown, AlertTriangle } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { missedPlayoffsCopy, isPlayoffFieldFinal } from "@/lib/playoffField";
import { initialsFor } from "@/components/dashboard/TeamSwatch";

const CHAMPS_BONUS = 10;

export default function NbaChampionsPickModal({ open, onClose, onSaved, tournamentId, participantId, teams, existingPicks, tournament }) {
    const [state, setState] = useState(null);
    const [picks, setPicks] = useState(existingPicks || {});
    const [saving, setSaving] = useState(false);

    // iter87.300 — same fix as `MlbSeriesPickModal.jsx` (see its
    // comment): only resync from `existingPicks` when the modal OPENS.
    const existingPicksRef = useRef(existingPicks);
    existingPicksRef.current = existingPicks;

    useEffect(() => {
        if (!open || !tournamentId) return;
        api.getNbaChampsState(tournamentId).then(setState).catch(() => setState(null));
        setPicks(existingPicksRef.current || {});
    }, [open, tournamentId]);

    if (!open) return null;
    const locked = !!state?.locked;
    // iter87.531 (user-requested — same addition as
    // MlbChampionsPickModal.jsx, see its comment) — proactive banner,
    // reusing the same shared `isPlayoffFieldFinal` signal the
    // reactive `missedPlayoffsCopy` notice below already uses.
    const fieldFinal = isPlayoffFieldFinal(tournament);
    const savedTeam = picks.champion ? (teams || []).find((t) => t.id === picks.champion) : null;
    const savedTeamDropped = !!(savedTeam && savedTeam.missed_playoffs);
    const otherTeams = (state?.teams || []).filter((t) => t.id !== picks.champion);

    const submitPick = async (teamId) => {
        setSaving(true);
        try {
            await api.submitNbaChampsPick(tournamentId, participantId, { champion: teamId });
            setPicks((p) => ({ ...p, champion: teamId }));
            onSaved?.(teamId);
            toast.success("Champions Pick saved");
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your Champions Pick");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="nba-champs-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-cyber/40 bg-obsidian p-4 sm:p-6 max-w-lg w-full max-h-[90dvh] overflow-y-auto"
        >
            <div className="flex items-center justify-between mb-3 gap-2">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-cyber flex items-center justify-center shrink-0">
                        <Crown className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">NBA Champions Pick · +{CHAMPS_BONUS} pts</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-nba-champs-pick-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            {locked && (
                <p data-testid="nba-champs-locked-notice" className="text-heat text-[11px] uppercase tracking-widest font-bold mb-3">
                    Locked — the Conference Finals matchups are set. Your pick below is final.
                </p>
            )}

            {!locked && !fieldFinal && (
                <div
                    data-testid="nba-champs-seeding-not-final-banner"
                    className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2 mb-3"
                >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        Seeding isn't final yet — this field is projected only and can still shift before the
                        last day of the regular season. Come back to make your final pick once the season ends
                        and seeding is official.
                    </span>
                </div>
            )}

            <div className="mb-4" data-testid="nba-champs-slot-champion">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                    Your pick
                </p>
                {savedTeam ? (
                    <div
                        data-testid={`nba-champs-current-${savedTeam.id}`}
                        className={`rounded-lg border p-3 flex items-center gap-3 ${
                            savedTeamDropped ? "border-heat/50 bg-heat/[0.06]" : "border-cyber/40 bg-cyber/[0.05]"
                        }`}
                    >
                        <div className="w-10 h-10 rounded-full border border-white/10 bg-white/[0.04] flex items-center justify-center text-xs font-bold text-zinc-300 shrink-0">
                            {initialsFor(savedTeam.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-base truncate">{savedTeam.name}</p>
                            {savedTeamDropped ? (
                                <p data-testid="nba-champs-dropped-notice" className="text-[10px] text-heat font-semibold flex items-center gap-1 mt-0.5">
                                    <AlertTriangle className="w-3 h-3 shrink-0" />
                                    {missedPlayoffsCopy(savedTeam.name, tournament, { locked, subject: "your pick" })}
                                </p>
                            ) : (
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">
                                    {locked ? "Locked in" : "Still alive"}
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-zinc-500 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
                        You haven't picked a champion yet.
                    </p>
                )}
            </div>

            {!locked && otherTeams.length > 0 && (
                <div data-testid="nba-champs-switch-section">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                        {savedTeam ? "Change your pick" : "Pick your champion"}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                        {otherTeams.map((t) => (
                            <motion.button
                                key={t.id}
                                data-testid={`nba-champs-champion-option-${t.id}`}
                                disabled={saving}
                                onClick={() => submitPick(t.id)}
                                whileHover={{ y: -2, scale: 1.015 }}
                                whileTap={{ scale: 0.975 }}
                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                className="p-3 rounded-lg border border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-cyber/40 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="text-sm font-bold truncate">{t.name}</div>
                            </motion.button>
                        ))}
                    </div>
                </div>
            )}
        </BentoModal>
    );
}
